<template>
  <SfModal v-if="modalOpen" :size="modalSize" @close="close">
    <template #title>{{ title }}</template>

    <template #body>
      <form
        class="sf-form sf-form-full sf-below-0 sf-c-text"
        data-walkthrough-id="new-remark-modal"
      >
        <RemarkTypeSelector v-if="!hideTypeSelector" v-model="type" />

        <div class="sf-grid sf-grid-fluid">
          <div class="sf-row">
            <div class="sf-col">
              <div v-if="showPeriodSelector" class="sf-level sf-v-pad-1">
                <div class="sf-level-item">
                  <div class="sf-font-s sf-title">
                    {{ visibilityLabel }}
                  </div>
                </div>
                <div class="sf-level-item">
                  <PeriodVisibilityDropdown
                    :visibility="visibility"
                    :ledger-label="ledgerLabel"
                    :is-todo="isTodo"
                    @change-visibility="changeVisibility"
                  ></PeriodVisibilityDropdown>
                </div>
                <div class="sf-level-item">
                  <div class="sf-font-xs sf-dim-el">
                    {{ visibilityExplainLabel }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="sf-row">
            <div class="sf-col">
              <MarkdownEditorSection
                v-model="text"
                :errors="commsErrors.text"
                :mention-usernames="mentionUsernames"
                :mention-accounts="mentionAccounts"
                :mention-recon-templates="mentionReconTemplates"
                :is-loading-accounts="isLoadingAccounts"
                :is-loading-recon-templates="isLoadingReconTemplates"
                :pulse-usernames="pulseUsernames"
                :period-private="periodPrivate"
                :period-name="ledgerLabel"
                :square-bottom="showMentionHint"
                @hint-visibility-changed="hintVisibilityChanged"
              />
            </div>
          </div>
          <div class="sf-row">
            <div class="sf-col">
              <div class="sf-level sf-level-between">
                <div class="sf-level-item">
                  <div class="sf-level sf-block">
                    <span
                      v-for="attachment in allAttachments"
                      :key="attachment.id"
                    >
                      <div
                        class="
                          sf-label sf-label-subtle sf-below-1 sf-level-item
                        "
                      >
                        <div class="sf-level sf-level-small">
                          <div class="sf-level-item">
                            {{ attachment.filename }}
                          </div>
                          <div
                            class="sf-level-item fa fa-times sf-pointer"
                            @click.prevent="handleDeleteAttachment(attachment)"
                          ></div>
                        </div>
                      </div>
                    </span>
                  </div>
                </div>
                <DirectUploadDropzone
                  :multiple="true"
                  :on-file-progress="updateUploadProgress"
                  :on-file-uploaded="addLocalAttachment"
                  ><span
                    class="sf-pointer sf-a"
                    data-walkthrough-id="new-remark-modal-add-attachment"
                    >{{
                      I18n.t('communication.remark_form.add_attachments')
                    }}</span
                  ></DirectUploadDropzone
                >
              </div>
            </div>
          </div>
          <div v-if="anyUnarchivedTags && showTags" class="sf-row">
            <div class="sf-col">
              <div class="sf-level sf-level-between">
                <RemarkTags
                  v-model="tagIds"
                  :all-tags="allRemarkTags"
                  :is-client-meeting-remark="isClientMeetingRemark"
                ></RemarkTags>
              </div>
            </div>
          </div>
          <div class="sf-row">
            <div class="sf-col">
              <div class="sf-level sf-level-between">
                <div class="sf-level">
                  <div class="sf-level-item">
                    <div
                      v-if="uploadInProgress"
                      class="sf-progress sf-progress-tiny sf-bg-snow"
                      style="width: 60px"
                    >
                      <div
                        :style="{ width: uploadProgress + '%' }"
                        class="sf-progress-complete sf-bg-primary"
                      ></div>
                    </div>
                  </div>
                </div>
                <SfButtonDs
                  variant="primary"
                  :disabled="uploadInProgress"
                  data-walkthrough-id="new-remark-modal-save"
                  :data-heap="heapAnalytics"
                  @click="save"
                  >{{ saveButtonLabel }}</SfButtonDs
                >
              </div>
            </div>
          </div>
          <div v-if="hasErrors('base')">
            <SfInputError v-for="e in commsErrors.base" :key="e">
              {{ e }}
            </SfInputError>
          </div>
        </div>
      </form>
    </template>
  </SfModal>
</template>

<script>
import { SfButtonDs } from '@silverfin/ui-components'
import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia'
import { SfModal } from '../sf-modal'
import { SfInputError } from '../sf-input/index.ts'
import api from '@app/application/communication/sections/communication/api/remark'
import attachmentsHelper from '@app/application/communication/sections/communication/components/mixins/attachments-helper'
import DirectUploadDropzone from '@app/shared/vue/components/direct-upload/direct-upload-dropzone'
import { remarkDraftMixin, remarkTagsMixin, commonMethodsMixin } from './mixins'
import RemarkTags from './remark-tags'
import { REMARK_ACTIONS } from '@app/application/communication/constants'
import truncate from '@app/utils/truncate'
import useCommsError from '@app/application/communication/sections/communication/composables/use-comms-form-errors'
import PeriodVisibilityDropdown from './period-visibility-dropdown.vue'
import { VisibilityLevels } from './constants'
import RemarkTypeSelector from './remark-type-selector.vue'
import MarkdownEditorSection from './markdown-editor-section.vue'
import { useMentionablesStore } from '@app/application/communication/store/mentionables'
import { isCurrentPeriodPrivate } from '@app/adapters/silverfin'

export default {
  components: {
    SfButtonDs,
    SfModal,
    SfInputError,
    DirectUploadDropzone,
    RemarkTags,
    PeriodVisibilityDropdown,
    RemarkTypeSelector,
    MarkdownEditorSection,
  },
  mixins: [
    attachmentsHelper,
    remarkDraftMixin,
    remarkTagsMixin,
    commonMethodsMixin,
  ],
  setup() {
    const { errors, hasErrors, handleErrors, clearTextErrors } = useCommsError()

    return {
      commsErrors: errors.errors,
      hasErrors,
      handleErrors,
      clearTextErrors,
      title: I18n.t('communication.add_remark'),
      saveButtonLabel: I18n.t('communication.remark_form.save'),
      visibilityLabel: I18n.t('communication.remark_form.sticky_label'),
    }
  },

  data() {
    return {
      ownerType: null,
      ownerId: null,
      ledgerId: null,
      ledgerLabel: null,
      endLedgerId: null,
      endLedgerLabel: null,
      visibility: VisibilityLevels.currentPeriod,
      type: 'note',
      text: null,
      attachments: [],
      mentionUsernames: [],
      pulseUsernames: [],
      periodPrivate: isCurrentPeriodPrivate(),
      modalOpen: false,
      savingInProgress: false,
      allAttachments: [],
      uploadProgress: 0,
      tagIds: [],
      hideTypeSelector: false,
      hidePeriodSelector: false,
      showMentionHint: false,
      modalSize: 's',
      reportElementId: null,
    }
  },

  computed: {
    ...mapPiniaState(useMentionablesStore, [
      'mentionAccounts',
      'mentionReconTemplates',
      'isLoadingAccounts',
      'isLoadingReconTemplates',
    ]),
    attachmentParams() {
      return this.allAttachments.map((attachment) => {
        return {
          documentDirectUploadPath: attachment.uploadPath,
        }
      })
    },

    allAttachmentsTruncated() {
      return this.allAttachments.map((attachment) => ({
        ...attachment,
        filename: truncate(attachment.filename),
      }))
    },
    heapAnalytics() {
      if (!this.isTodo && this.isPinnedChosenForVisibility) {
        return 'pinned-note-creation'
      }

      if (this.isTodo && this.isPinnedChosenForVisibility) {
        return 'pinned-to-do-creation'
      }

      if (this.isTodo && this.isRecurringChosenForVisibility) {
        return 'periodic-to-do-creation'
      }

      return null
    },
  },

  watch: {
    text() {
      this.clearTextErrors()
    },
  },

  methods: {
    ...mapPiniaActions(useMentionablesStore, ['initialize']),
    // ModalCitizen interface
    async openModal(context) {
      this.ownerType = context.ownerType
      this.ownerId = context.ownerId
      this.ledgerId = context.ledgerId || window.Silverfin.currentLedgerId()
      this.ledgerLabel = context.ledgerLabel
      this.clientMeetingId = context.clientMeetingId
      this.textPropertyValue = context.textPropertyValue
      this.liquidInputFieldPath = context.liquidInputFieldPath
      this.tagIds = context.selectedTagIds || []
      this.allRemarkTags = context.allRemarkTags
      // for a new remark, the modal context text takes precedence
      // (i.e. highlighted text is quoted in a Remark automatically)
      this.text = context.text || this.loadDraft()
      this.showTags = context.showTags
      this.pdfAnnotationPayload = context.pdfAnnotation
      this.hideTypeSelector = context.hideTypeSelector
      this.title = context.title || I18n.t('communication.add_remark')
      this.hidePeriodSelector = context.hidePeriodSelector
      this.saveButtonLabel =
        context.saveButtonLabel || I18n.t('communication.remark_form.save')
      this.type = context.type || 'note'
      this.callbackPayload = context.callbackPayload
      this.modalSize = context.modalSize || 's'
      this.isClientMeetingRemark = context.isClientMeetingRemark
      this.reportElementId = context.reportElementId || null
      await this.setMentionableUsernames()
      this.initialize(
        context.companyId || window.Silverfin.currentCompanyId,
        this.ledgerId
      )
      this.modalOpen = context.ownerId && context.ownerType
    },
    resetRemarkData() {
      this.type = 'note'
      this.text = null
      this.visibility = VisibilityLevels.currentPeriod
      this.allAttachments = []
      this.reportElementId = null
    },
    save() {
      if (this.savingInProgress) {
        return
      }

      this.savingInProgress = true

      const params = {
        ownerType: this.ownerType,
        ownerId: this.ownerId,
        text: this.text,
        ledgerId: this.ledgerId,
        todo: this.isTodo,
        sticky: this.isPinnedChosenForVisibility,
        recurring: this.isRecurringChosenForVisibility,
        fileAttrs: this.attachmentParams,
        textPropertyValue: this.textPropertyValue,
        liquidInputFieldPath: this.liquidInputFieldPath,
        tagIds: this.tagIds,
        pdfAnnotationAttributes: this.pdfAnnotationPayload,
        clientMeetingId: this.clientMeetingId,
        reportElementId: this.reportElementId,
      }

      api
        .create(params)
        .then(({ remark }) => {
          window.eventHub.$emit('refreshCommunicationData', {
            remark,
            action: REMARK_ACTIONS.CREATED,
            callbackPayload: this.callbackPayload,
          })
          window.eventHub.$emit('scrollToElement', {
            elementType: 'remark',
            elementId: remark.id,
          })
          this.close()
          this.resetRemarkData()
        })
        .catch(this.handleErrors)
        .finally(() => {
          this.savingInProgress = false
          this.cleanupDraft()
        })
    },

    handleDeleteAttachment(attachment) {
      const index = this.allAttachments.indexOf(attachment)
      this.allAttachments.splice(index, 1)
    },

    addLocalAttachment({ blob }) {
      this.allAttachments.push({
        id: blob.id,
        filename: blob.filename,
        type: blob.content_type,
        size: blob.byte_size,
        uploadPath: blob.signed_id,
        isTemp: true,
      })
    },
    setNote() {
      if (this.isRecurringChosenForVisibility) {
        this.visibility = VisibilityLevels.currentPeriod
      }
      this.type = 'note'
    },
    hintVisibilityChanged(value) {
      this.showMentionHint = value
    },
  },
}
</script>
